import React, { useMemo, useState, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import "dayjs/locale/en";

import { useSubscriptions } from "../../hooks/useSubscriptions.tsx";
import AddSubscriptionForm from "../../components/AddSubscription/AddSubscriptionForm.tsx";
import Input from "../../components/ui/input.tsx";
import Button from "../../components/ui/button.tsx";
import { SimpleSelect } from "../../components/ui/select-shadcn.tsx";
import {
  BadgeCheck,
  BadgeX,
  Pause,
  Play,
  Plus,
  SlidersHorizontal,
  X,
} from "lucide-react";
import Modal from "../../components/Modal/index.tsx";
import Label from "../../components/ui/label.tsx";
import { categories } from "../../data/categories.tsx";
import { transactionTypes } from "../../data/transactionTypes.tsx";
import { useSearchParams } from "react-router-dom";
import { useExchangeRates } from "../../hooks/useExchangeRates.tsx";

dayjs.locale("en");

function nextDueDate(startDate: string, recurrence: string, endDate?: string) {
  try {
    const now = dayjs().startOf("day");
    let cur = dayjs(startDate).startOf("day");
    const end = endDate ? dayjs(endDate).startOf("day") : null;
    const step = (d: any) => {
      if (recurrence === "weekly") return d.add(1, "week");
      if (recurrence === "yearly") return d.add(1, "year");
      return d.add(1, "month");
    };
    if (cur.isAfter(now)) return cur;
    let guard = 0;
    while (cur.isBefore(now) && guard < 480) {
      cur = step(cur);
      guard++;
      if (end && cur.isAfter(end)) return null;
    }
    return end && cur.isAfter(end) ? null : cur;
  } catch {
    return null;
  }
}

export default function Subscriptions() {
  const [showModal, setShowModal] = useState(false);
  const [filterType, setFilterType] = useState<string>("all");
  const [searchText, setSearchText] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [period, setPeriod] = useState<string>("month");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const FILTERS_STORAGE_KEY = "filters:subscriptions";
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    subscriptions,
    addSubscription,
    updateSubscription,
    deleteSubscription,
  } = useSubscriptions();
  const BASE_CURRENCY = (() => {
    try {
      return localStorage.getItem("pref:baseCurrency") || "AED";
    } catch {
      return "AED";
    }
  })();
  const { rates } = useExchangeRates(BASE_CURRENCY);

  // Initialize filters: URL params take precedence, then localStorage
  useEffect(() => {
    const hasURL = ["s", "t", "c", "p", "f", "to"].some((k) =>
      searchParams.has(k)
    );
    if (hasURL) {
      const s = searchParams.get("s") ?? "";
      const t = searchParams.get("t") ?? "all";
      const c = searchParams.get("c") ?? "all";
      const p = searchParams.get("p") ?? "month";
      const f = searchParams.get("f") ?? "";
      const toq = searchParams.get("to") ?? "";
      setSearchText(s);
      setFilterType(t);
      setSelectedCategory(c);
      setPeriod(p);
      setDateFrom(f);
      setDateTo(toq);
      return;
    }
    try {
      const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (raw) {
        const f = JSON.parse(raw);
        setSearchText(f.searchText ?? "");
        setFilterType(f.filterType ?? "all");
        setSelectedCategory(f.selectedCategory ?? "all");
        setPeriod(f.period ?? "month");
        setDateFrom(f.dateFrom ?? "");
        setDateTo(f.dateTo ?? "");
      }
    } catch {}
  }, [searchParams]);

  // Persist filters
  useEffect(() => {
    try {
      const f = {
        searchText,
        filterType,
        selectedCategory,
        period,
        dateFrom,
        dateTo,
      };
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(f));
    } catch {}
  }, [
    searchText,
    filterType,
    selectedCategory,
    period,
    dateFrom,
    dateTo,
    setSearchParams,
  ]);

  // Sync filters to URL
  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchText.trim() !== "") params.s = searchText.trim();
    if (filterType !== "all") params.t = filterType;
    if (selectedCategory !== "all") params.c = selectedCategory;
    if (period !== "month") params.p = period;
    if (period === "range") {
      if (dateFrom) params.f = dateFrom;
      if (dateTo) params.to = dateTo;
    }
    setSearchParams(params, { replace: true });
  }, [
    searchText,
    filterType,
    selectedCategory,
    period,
    dateFrom,
    dateTo,
    setSearchParams,
  ]);

  const filteredTransactions = useMemo(() => {
    return subscriptions
      .filter((tx) => (filterType === "all" ? true : tx.type === filterType))
      .filter((tx) =>
        selectedCategory && selectedCategory !== "all"
          ? tx.category === selectedCategory
          : true
      )
      .filter((tx) =>
        tx.title.toLowerCase().includes(searchText.toLowerCase())
      );
  }, [subscriptions, filterType, selectedCategory, searchText]);

  const convertToBase = useCallback(
    (amount: number, from: string) => {
      if (!rates) return 0;
      const r = rates as any;
      const base = r[BASE_CURRENCY.toLowerCase()];
      const src = r[(from || "").toLowerCase()];
      if (!base || !src) return 0;
      return amount * (base / src);
    },
    [rates, BASE_CURRENCY]
  );

  const monthlyTotal = useMemo(() => {
    return filteredTransactions
      .filter((s) => s.isActive !== false)
      .reduce((acc, s) => acc + convertToBase(s.amount, s.currency), 0);
  }, [filteredTransactions, convertToBase]);

  return (
    <section className="p-5 flex justify-center">
      <div className="container-site">
        {/* Header */}
        <div className="flex flex-wrap gap-5 justify-between items-center mb-4">
          <div>
            <h3 className="heading-2">Subscriptions</h3>
            <p className="sub-heading-2">Manage your financial subscriptions</p>
          </div>

          <div className="flex items-center gap-2">
            <Input
              type="text"
              placeholder="Search title..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-56"
            />
            <button
              onClick={() => setShowModal(true)}
              aria-label="Add subscription"
              title="Add Subscription"
              className="btn-ghost p-2"
            >
              <Plus className="h-4 w-4" />
            </button>
            <button
              aria-label="Open filters"
              title="Filters"
              onClick={() => setShowFilters(true)}
              className="btn-ghost p-2"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {useMemo(() => {
                let count = 0;
                if (searchText.trim() !== "") count++;
                if (filterType !== "all") count++;
                if (selectedCategory !== "all") count++;
                if (period !== "month") count++;
                if (period === "range" && (dateFrom || dateTo)) count++;
                return count > 0 ? (
                  <span className="absolute -top-1 -right-1 min-w-[1.1rem] h-4 px-1 rounded-full bg-indigo-600 text-[10px] leading-4 text-white text-center">
                    {count}
                  </span>
                ) : null;
              }, [
                searchText,
                filterType,
                selectedCategory,
                period,
                dateFrom,
                dateTo,
              ])}
            </button>
          </div>
        </div>

        {/* Filters Modal */}
        <Modal
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          title="Filters"
        >
          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              setShowFilters(false);
            }}
          >
            {/* Quick Period */}
            <div>
              <Label className="mb-2 block">Quick Period</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: "month", label: "This Month" },
                  { id: "7d", label: "Last 7 Days" },
                  { id: "30d", label: "Last 30 Days" },
                  { id: "all", label: "All Time" },
                  { id: "range", label: "Custom Range" },
                ].map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPeriod(p.id)}
                    className={[
                      "px-3 py-1.5 rounded-md text-sm border",
                      period === p.id
                        ? "bg-indigo-600 text-white border-indigo-500"
                        : "bg-white/5 text-white/90 border-white/15 hover:bg-white/10",
                    ].join(" ")}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Search moved to header for seamless typing */}

            {/* Type & Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block">Type</Label>
                <SimpleSelect
                  value={filterType}
                  onValueChange={(v) => setFilterType(v)}
                  options={[
                    { label: "All Types", value: "all" },
                    ...transactionTypes.map((t) => ({
                      label: t.name,
                      value: t.id,
                    })),
                  ]}
                />
              </div>
              <div>
                <Label className="mb-1 block">Category</Label>
                <SimpleSelect
                  value={selectedCategory}
                  onValueChange={(v) => setSelectedCategory(v)}
                  options={[
                    { label: "All Categories", value: "all" },
                    ...categories.map((c) => ({ label: c.name, value: c.id })),
                  ]}
                />
              </div>
            </div>

            {/* Date Range */}
            {period === "range" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="from" className="mb-1 block">
                    From
                  </Label>
                  <Input
                    id="from"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="to" className="mb-1 block">
                    To
                  </Label>
                  <Input
                    id="to"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
              </div>
            )}

            {useMemo(() => {
              if (period === "range" && dateFrom && dateTo) {
                const invalid = dayjs(dateFrom).isAfter(dayjs(dateTo));
                return invalid ? (
                  <p className="text-red-400 text-sm">
                    From date cannot be after To date.
                  </p>
                ) : null;
              }
              return null;
            }, [period, dateFrom, dateTo])}
            {/* Actions */}
            <div className="pt-2 flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setSearchText("");
                  setFilterType("all");
                  setSelectedCategory("all");
                  setPeriod("month");
                  setDateFrom("");
                  setDateTo("");
                }}
              >
                Reset
              </Button>
              <Button type="submit">Apply</Button>
            </div>
          </form>
        </Modal>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="border border-dashed border-white/10 bg-white/5 backdrop-blur p-4">
            <div className="text-white/80 text-sm">Monthly Total (est.)</div>
            <div className="text-3xl font-semibold text-white mt-1">
              {BASE_CURRENCY} {monthlyTotal.toFixed(2)}
            </div>
            <div className="text-white/60 text-xs mt-1">
              Active subscriptions only
            </div>
          </div>
        </div>

        {filteredTransactions.length === 0 ? (
          <p className="text-slate-500 text-center py-6">
            No matching subscriptions found.
          </p>
        ) : (
          <div className="space-y-3">
            {filteredTransactions
              .filter((n: any) => n.isActive)
              .map((s) => {
                const due = nextDueDate(
                  s.startDate,
                  s.recurrenceType || "monthly",
                  s.endDate
                );
                const cat = categories.find((c) => c.id === s.category);
                const isPaused = s.isActive === false;
                return (
                  <div
                    key={s.id}
                    className="card-glass p-4 flex items-center justify-between gap-4 bg-white/5 backdrop-blur "
                  >
                    <div className="flex gap-4">
                      <div className="w-12 flex justify-center items-center">
                        <BadgeCheck />
                      </div>
                      <div>
                        <div className="text-white font-medium">{s.title}</div>
                        <div className="text-white/70 text-sm">
                          {cat?.name || s.category} ·{" "}
                          {s.recurrenceType || "monthly"}
                          {due && !isPaused
                            ? ` · Next due ${dayjs(due).format("DD MMM YYYY")}`
                            : ""}
                          {isPaused ? " · Paused" : ""}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-white font-semibold mr-2">
                        {BASE_CURRENCY}{" "}
                        {convertToBase(s.amount, s.currency).toFixed(2)}
                      </div>
                      <button
                        className="btn-ghost p-2 text-white/90 border border-white/15 rounded-md"
                        onClick={() =>
                          updateSubscription({
                            ...s,
                            isActive: !s.isActive,
                          } as any)
                        }
                        aria-label={
                          isPaused
                            ? "Resume subscription"
                            : "Pause subscription"
                        }
                      >
                        {isPaused ? (
                          <Play className="h-4 w-4" />
                        ) : (
                          <Pause className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        className="btn-ghost p-2 text-white/90 border border-red-400/30 hover:border-red-400/60 rounded-md"
                        onClick={() => deleteSubscription(s.id)}
                        aria-label="Cancel subscription"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}

            {filteredTransactions
              .filter((n: any) => !n.isActive)
              .map((s) => {
                const due = nextDueDate(
                  s.startDate,
                  s.recurrenceType || "monthly",
                  s.endDate
                );
                const cat = categories.find((c) => c.id === s.category);
                const isPaused = s.isActive === false;
                return (
                  <div
                    key={s.id}
                    className="card-glass p-4 flex items-center justify-between gap-4 bg-white/5 backdrop-blur "
                  >
                    <div className="flex gap-4">
                      <div className="w-12 flex justify-center items-center">
                        <BadgeX />
                      </div>
                      <div>
                        <div className="text-white font-medium">{s.title}</div>
                        <div className="text-white/70 text-sm">
                          {cat?.name || s.category} ·{" "}
                          {s.recurrenceType || "monthly"}
                          {due && !isPaused
                            ? ` · Next due ${dayjs(due).format("DD MMM YYYY")}`
                            : ""}
                          {isPaused ? " · Paused" : ""}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-white font-semibold mr-2">
                        {BASE_CURRENCY}{" "}
                        {convertToBase(s.amount, s.currency).toFixed(2)}
                      </div>
                      <button
                        className="btn-ghost p-2 text-white/90 border border-white/15 rounded-md"
                        onClick={() =>
                          updateSubscription({
                            ...s,
                            isActive: !s.isActive,
                          } as any)
                        }
                        aria-label={
                          isPaused
                            ? "Resume subscription"
                            : "Pause subscription"
                        }
                      >
                        {isPaused ? (
                          <Play className="h-4 w-4" />
                        ) : (
                          <Pause className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        className="btn-ghost p-2 text-white/90 border border-red-400/30 hover:border-red-400/60 rounded-md"
                        onClick={() => deleteSubscription(s.id)}
                        aria-label="Cancel subscription"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title="Add Subscription"
        >
          <AddSubscriptionForm
            onSubmit={(data) => {
              const payload = {
                id: uuidv4(),
                title: data.title,
                type: data.type,
                description: data.description,
                amount: Number(data.amount || 0),
                category: data.category,
                startDate: data.startDate,
                endDate: data.endDate || "",
                currency: data.currency,
                notes: data.notes || "",
                recurrenceType: data.recurrenceType || "monthly",
                isActive: data.isActive ?? true,
              };
              addSubscription(payload as any);
              setShowModal(false);
            }}
            onError={(e) => console.error(e)}
          />
        </Modal>
      </div>
    </section>
  );
}
